import { FormikErrors, useFormikContext } from "formik";
import React from "react";
import _ from "lodash";

import { TFormValues, TStepNavigation } from "./types";
import { GuideContext } from "../../components/GatewayForm/GatewayForm.store";
import Button from "../Button/Button";

type TStepNavigationProps = {
  previous?: TStepNavigation;
  next?: TStepNavigation;
  submit?: boolean | undefined | ((values: TFormValues) => boolean);
  onSubmit?: (values: TFormValues) => any;
  stepText?: string;
  scrollToTop: () => void;
  customButtonText?: string;
  submitLoading?: boolean;
};

const StepNavigation: React.FC<TStepNavigationProps> = ({
  previous,
  next,
  submit,
  onSubmit,
  stepText,
  scrollToTop,
  customButtonText,
  submitLoading
}) => {
  const { setCurrentStep, currentConfig, currentStep, analytics } = React.useContext(GuideContext);
  const { values, validateForm, setTouched } = useFormikContext<TFormValues>();
  const [errors, setErrors] = React.useState<FormikErrors<TFormValues>>({});

  const stepConfig = React.useMemo(() => {
    return currentConfig.steps[currentStep];
  }, [currentConfig.steps, currentStep]);

  const validate = async (): Promise<boolean> => {
    // run validation
    const errors = await validateForm();

    setErrors(errors);

    // mark fields touched
    if (!_.isEmpty(errors)) {
      // Mark error fields as touched so error message displays
      setTouched(
        Object.keys(errors).reduce((a, c) => {
          return {
            ...a,
            [c]: true,
          };
        }, {})
      );

      return false;
    }

    // form is valid
    return true;
  };

  const handleNextButton = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (stepConfig.next === undefined) {
      return;
    }

    // check if valid
    const isValid = await validate();
    if (!isValid) {
      return;
    }

    let goToValue = stepConfig.next;

    if (typeof goToValue === "function") {
      goToValue = goToValue(values);
    }

    const stepNumber = currentConfig.steps[currentStep].stepNumber;
    analytics.stepCompleted(currentStep, stepNumber, values);

    // Set eligibility in GA
    if (goToValue === "/apply/no") {
      analytics.setEligibility("ineligible");
    } else if (goToValue === "eligible-step-one") {
      analytics.setEligibility("eligible");
    }

    // Submit form for steps that aren't submit to backend steps
    if (
      ["/legal-resources-results", "court-step-one", "protesting-step-one", "legal-step-four"].includes(
        goToValue
      )
    ) {
      analytics.submitForm();
    }

    if (["court-step-one", "protesting-step-one", "legal-step-four"].includes(goToValue)) {
      const formStep = goToValue === "legal-step-four" ? "priority-step-one" : goToValue;
      analytics.formStarted(formStep);
    }

    if (typeof goToValue === "string" && goToValue.startsWith("/")) {
      window.sessionStorage.setItem("currentFormStep", "");
      window.location.href = goToValue;
    } else {
      setCurrentStep(goToValue);
    }

    scrollToTop();
  };

  const handleSubmitButton = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // check if valid
    const isValid = await validate();
    if (!isValid) {
      return;
    }
    if (onSubmit) {
      onSubmit(values);
    }

    analytics.submitForm();
  };

  const isSubmit = () => {
    let submitValue = submit;

    if (typeof submitValue === "function") {
      submitValue = submitValue(values);
    }

    return submitValue;
  };

  return (
    <>
      {/* {errors &&
        Object.keys(errors).map((errorKey) => (
          <p key={errorKey} style={{ color: "red" }}>
            {errors[errorKey]}
          </p>
        ))} */}
      {isSubmit() ? (
        <div className="nextButton container">
          <Button text="Submit" onClick={handleSubmitButton} secondaryText={stepText} disabled={submitLoading} />
        </div>
      ) : (
        <div className="nextButton container">
          <Button
            text={customButtonText ? customButtonText : "Next"}
            onClick={handleNextButton}
            secondaryText={stepText}
            disabled={submitLoading}
          />
        </div>
      )}
    </>
  );
};

export default StepNavigation;
